import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import SearchBar from "~/components/searchBar";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

const CategoryPicker = ({
  categories = [],
  value,
  onChange,
  placeholder = "Select Category",
  className = "",
  disabled = false,
  forceDownward = false,
  portalToBody = false,
}) => {
  const [showCategories, setShowCategories] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [expandedCategoryCode, setExpandedCategoryCode] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const categoryRef = useRef(null);
  const dropdownRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState(null);

  useEffect(() => {
    setFilteredCategories(categories);
  }, [categories]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        portalToBody &&
        dropdownRef.current &&
        dropdownRef.current.contains(event.target)
      ) {
        return;
      }
      if (
        showCategories &&
        categoryRef.current &&
        !categoryRef.current.contains(event.target)
      ) {
        setShowCategories(false);
        setExpandedCategoryCode(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCategories, portalToBody]);

  const updatePortalStyle = () => {
    if (!portalToBody || !showCategories || !categoryRef.current) return;
    const triggerRect = categoryRef.current.getBoundingClientRect();
    setPortalStyle({
      position: "fixed",
      top: triggerRect.bottom + 4,
      left: triggerRect.left,
      width: triggerRect.width,
      zIndex: 10000,
    });
  };

  useEffect(() => {
    if (!showCategories || !categoryRef.current) return;

    if (portalToBody) {
      setOpenUpward(false);
      updatePortalStyle();
      return;
    }

    if (forceDownward) {
      setOpenUpward(false);
      return;
    }

    const triggerRect = categoryRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.offsetHeight || 260;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
  }, [showCategories, filteredCategories.length, forceDownward, portalToBody]);

  useEffect(() => {
    if (!portalToBody || !showCategories) return;
    const handler = () => updatePortalStyle();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [portalToBody, showCategories]);

  useEffect(() => {
    if (!showCategories || !categoryRef.current) return;

    const triggerRect = categoryRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.offsetHeight || 260;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
  }, [showCategories, filteredCategories.length]);

  const handleCategoryToggle = (cat) => {
    setExpandedCategoryCode((prev) => (prev === cat?.code ? null : cat?.code));
  };

  const handleSubCategorySelect = (parentCat, sub) => {
    onChange?.({ value: sub?.code });
    setShowCategories(false);
    setFilteredCategories(categories);
    setExpandedCategoryCode(null);
  };

  const searchEntity = (searchValue) => {
    if (!searchValue) {
      setFilteredCategories(categories);
      return;
    }
    const filtered = categories.filter((cat) =>
      cat?.name?.toLowerCase().includes(searchValue.toLowerCase()),
    );
    setFilteredCategories(filtered);
  };

  const selectedSubName = (() => {
    if (!value) return null;
    for (const cat of categories) {
      const sub = cat?.subCategory?.find((s) => s?.code === value);
      if (sub) return sub.name;
    }
    return null;
  })();

  return (
    <div
      className={`category-picker-container ${className} ${
        !value ? "is-placeholder" : ""
      }`}
      ref={categoryRef}
    >
      <Button
        outlined
        icon="pi pi-angle-down"
        iconPos="right"
        className="category-picker-btn"
        placeholder={placeholder}
        onClick={() => {
          if (disabled) return;
          setShowCategories((prev) => !prev);
          if (!showCategories) setFilteredCategories(categories);
        }}
        disabled={disabled}
      >
        <span className="category-picker-selected">
          {selectedSubName || placeholder}
        </span>
      </Button>
      {showCategories &&
        (portalToBody ? (
          createPortal(
            <div
              ref={dropdownRef}
              style={portalStyle || undefined}
              className={`category-picker-dropdown category-tree-container ${
                !forceDownward && openUpward ? "open-upward" : ""
              }`}
            >
              <div className="search-container">
                <SearchBar onChange={(e) => searchEntity(e.target.value)} />
              </div>
              <ul className="category-tree">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((cat, index) => (
                    <li key={cat?.code ?? index} className="category-tree-node">
                      <div
                        className={`category-tree-parent ${
                          expandedCategoryCode === cat?.code ? "expanded" : ""
                        }`}
                        onClick={() => handleCategoryToggle(cat)}
                      >
                        <i
                          className={`pi ${
                            expandedCategoryCode === cat?.code
                              ? "pi-chevron-down"
                              : "pi-chevron-right"
                          }`}
                        />
                        <i
                          className={`pi ${
                            expandedCategoryCode === cat?.code
                              ? "pi-folder-open"
                              : "pi-folder"
                          }`}
                        />
                        <span>{cat?.name}</span>
                      </div>
                      {expandedCategoryCode === cat?.code && (
                        <ul className="category-tree-children">
                          {cat?.subCategory?.map((sub, subIndex) => (
                            <li
                              key={sub?.code ?? subIndex}
                              className={`category-tree-child ${
                                value === sub?.code ? "selected" : ""
                              }`}
                              onClick={() => handleSubCategorySelect(cat, sub)}
                            >
                              <i className="pi pi-folder" />
                              <span>{sub?.name}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))
                ) : (
                  <p className="empty-search">No results found</p>
                )}
              </ul>
            </div>,
            document.body,
          )
        ) : (
          <div
            ref={dropdownRef}
            className={`category-picker-dropdown category-tree-container ${
              !forceDownward && openUpward ? "open-upward" : ""
            }`}
          >
            <div className="search-container">
              <SearchBar onChange={(e) => searchEntity(e.target.value)} />
            </div>
            <ul className="category-tree">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((cat, index) => (
                  <li key={cat?.code ?? index} className="category-tree-node">
                    <div
                      className={`category-tree-parent ${
                        expandedCategoryCode === cat?.code ? "expanded" : ""
                      }`}
                      onClick={() => handleCategoryToggle(cat)}
                    >
                      <i
                        className={`pi ${
                          expandedCategoryCode === cat?.code
                            ? "pi-chevron-down"
                            : "pi-chevron-right"
                        }`}
                      />
                      <i
                        className={`pi ${
                          expandedCategoryCode === cat?.code
                            ? "pi-folder-open"
                            : "pi-folder"
                        }`}
                      />
                      <span>{cat?.name}</span>
                    </div>
                    {expandedCategoryCode === cat?.code && (
                      <ul className="category-tree-children">
                        {cat?.subCategory?.map((sub, subIndex) => (
                          <li
                            key={sub?.code ?? subIndex}
                            className={`category-tree-child ${
                              value === sub?.code ? "selected" : ""
                            }`}
                            onClick={() => handleSubCategorySelect(cat, sub)}
                          >
                            <i className="pi pi-folder" />
                            <span>{sub?.name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))
              ) : (
                <p className="empty-search">No results found</p>
              )}
            </ul>
          </div>
        ))}
    </div>
  );
};

CategoryPicker.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      code: PropTypes.string,
      subCategory: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          code: PropTypes.string,
        }),
      ),
    }),
  ),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  forceDownward: PropTypes.bool,
  portalToBody: PropTypes.bool,
};

export default CategoryPicker;
